// CALEUMS ring size picker (audit U6, owner-chosen layout "A").
//
// <caleums-size-field> wraps the compact "Select your size" field and its size sheet (a Prestige <x-modal>, which
// gives the bottom sheet on mobile, the centred modal on desktop, the focus trap, Esc to close, scroll lock and
// aria-expanded on the field button). The variant picker's own radios stay in the form, hidden; picking a chip checks
// the matching radio and fires "change", so Prestige's <variant-picker> selects the variant, updates the URL, price,
// availability and buy buttons exactly as if the radio had been clicked.
//
// <caleums-size-guide> runs the "Size chart" / "How to measure" tabs inside the size guide modal.
//
// Markup: snippets/caleums-ring-size-field.liquid, caleums-ring-size-guide.liquid, variant-picker.liquid.

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SYSTEMS = ['us', 'eu', 'uk'];
const SYSTEM_KEY = 'caleums:ring-size-system';

function readSystem() {
  try {
    const value = window.localStorage.getItem(SYSTEM_KEY);
    return SYSTEMS.includes(value) ? value : 'us';
  } catch (error) {
    return 'us';
  }
}

function saveSystem(value) {
  try {
    window.localStorage.setItem(SYSTEM_KEY, value);
  } catch (error) {
    // Storage blocked: the choice simply is not remembered.
  }
}

// Prestige's modals slide or fade in with the Web Animations API, which ignores prefers-reduced-motion. For our modals,
// swap in an instant animation when the visitor asks for reduced motion.
function respectReducedMotion(modal) {
  if (!modal || modal.caleumsMotionPatched) {
    return;
  }

  modal.caleumsMotionPatched = true;

  const instant = (element) => element.animate([], { duration: 0 });

  for (const method of ['createEnterAnimationControls', 'createLeaveAnimationControls']) {
    modal[method] = function () {
      if (reducedMotion.matches) {
        const animation = instant(this);
        return { finished: animation.finished, finish: () => animation.finish() };
      }

      return Object.getPrototypeOf(this)[method].call(this);
    };
  }
}

class CaleumsSizeField extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.button = this.querySelector('[data-caleums-size-field]');
    this.label = this.querySelector('[data-caleums-size-label]');
    this.hint = this.querySelector('[data-caleums-size-hint]');
    this.native = this.closest('fieldset')?.querySelector('[data-caleums-size-native]');
    // The sheet is moved to <body> while open, so keep a reference rather than querying inside the field.
    this.sheet = this.sheet || document.getElementById(this.getAttribute('sheet'));

    if (!this.button || !this.native || !this.sheet) {
      return;
    }

    respectReducedMotion(this.sheet);

    this.button.addEventListener('click', () => this.prepareSheet(), { signal });
    this.native.addEventListener('change', () => this.render(), { signal });
    // "invalid" does not bubble: listen in the capture phase. It fires when the form is submitted without a size.
    this.native.addEventListener('invalid', () => this.onInvalid(), { capture: true, signal });
    this.sheet.addEventListener('click', (event) => this.onSheetClick(event), { signal });
    this.form?.addEventListener('variant:change', () => this.render(), { signal });

    this.setSystem(readSystem(), false);
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get form() {
    return document.forms[this.getAttribute('form')];
  }

  get picker() {
    return this.closest('variant-picker');
  }

  get chips() {
    return Array.from(this.sheet.querySelectorAll('.caleums-size-chip'));
  }

  get checkedRadio() {
    return this.native.querySelector('input[type="radio"]:checked');
  }

  radioFor(value) {
    return Array.from(this.native.querySelectorAll('input[type="radio"]')).find((radio) => radio.value === value);
  }

  describe(chip, value) {
    if (!chip || !chip.dataset.eu) {
      return value;
    }

    return this.getAttribute('value-template')
      .replace('[us]', chip.dataset.us)
      .replace('[eu]', chip.dataset.eu)
      .replace('[uk]', chip.dataset.uk);
  }

  // Mirror the native radios (selection, sold out, hidden) on the field and the chips.
  render() {
    const checked = this.checkedRadio;
    const soldOutLabel = window.themeVariables?.strings?.soldOutButton;

    this.chips.forEach((chip) => {
      const radio = this.radioFor(chip.dataset.value);
      const swatch = radio && this.native.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      const soldOut = Boolean(swatch?.classList.contains('is-disabled') || radio?.disabled);

      chip.hidden = !radio || Boolean(swatch?.hidden);
      chip.classList.toggle('is-sold-out', soldOut);
      chip.setAttribute('aria-pressed', String(Boolean(checked && radio === checked)));
      chip.setAttribute('aria-label', this.describe(chip, chip.dataset.value) + (soldOut && soldOutLabel ? ` (${soldOutLabel})` : ''));
    });

    if (checked) {
      const chip = this.chips.find((item) => item.dataset.value === checked.value);
      this.label.textContent = this.describe(chip, checked.value);
      this.button.removeAttribute('data-empty');
      this.button.removeAttribute('aria-invalid');
      this.hint.hidden = true;
    } else {
      this.label.textContent = this.getAttribute('placeholder');
      this.button.setAttribute('data-empty', '');
      this.hint.hidden = false;
    }
  }

  // Runs before Prestige opens the sheet: on desktop, start keyboard focus on the chosen size (or the first one).
  prepareSheet() {
    this.render();

    const checked = this.checkedRadio;
    const target = (checked && this.chips.find((chip) => chip.dataset.value === checked.value && !chip.hidden)) || this.chips.find((chip) => !chip.hidden);

    if (target) {
      this.sheet.setAttribute('initial-focus', `.caleums-size-chip[data-value="${CSS.escape(target.dataset.value)}"]`);
    }
  }

  openSheet() {
    this.prepareSheet();
    this.sheet.show?.();
  }

  onInvalid() {
    // checkValidity() and reportValidity() each fire "invalid" on every radio of the group: react once.
    const now = Date.now();

    if (this.sheet.open || now - (this.lastInvalid || 0) < 1000) {
      return;
    }

    this.lastInvalid = now;
    this.button.setAttribute('aria-invalid', 'true');
    this.openSheet();
  }

  onSheetClick(event) {
    const system = event.target.closest('[data-caleums-size-system]');

    if (system) {
      this.setSystem(system.dataset.caleumsSizeSystem, true);
      return;
    }

    const chip = event.target.closest('.caleums-size-chip');

    if (chip) {
      this.pick(chip.dataset.value);
    }
  }

  setSystem(system, remember) {
    if (!SYSTEMS.includes(system)) {
      return;
    }

    this.sheet.querySelectorAll('[data-caleums-size-system]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.caleumsSizeSystem === system));
    });

    this.chips.forEach((chip) => {
      chip.textContent = chip.dataset[system] || chip.dataset.value;
    });

    if (remember) {
      saveSystem(system);
    }
  }

  async pick(value, attempt = 0) {
    const radio = this.radioFor(value);
    const picker = this.picker;

    if (!radio) {
      return;
    }

    // The variant picker attaches its listeners once the product JSON has loaded (usually long before anyone picks).
    if (picker && !picker.product && attempt < 50) {
      setTimeout(() => this.pick(value, attempt + 1), 100);
      return;
    }

    const previousId = picker?.selectedVariant?.id;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));

    const variant = picker?.selectedVariant;

    // Prestige skips its update when the variant id did not change. That happens when the first size is picked from
    // the "no size chosen yet" state, because the form already held that variant. Announce it ourselves so the buy
    // buttons leave "Select a size", and put the variant in the URL as Prestige would.
    if (picker && variant && variant.id === previousId) {
      this.form?.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { product: picker.product, variant, previousVariant: variant }
      }));

      if (picker.updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    }

    this.render();
    await this.sheet.hide?.();
  }
}

class CaleumsSizeGuide extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.caleumsGuideTabButton), { signal });
      tab.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    });

    respectReducedMotion(this.closest('x-modal'));
    this.select(this.selected || 'chart');
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  select(name, focus = false) {
    if (!this.tabs.some((tab) => tab.dataset.caleumsGuideTabButton === name)) {
      return;
    }

    this.selected = name;

    this.tabs.forEach((tab) => {
      const active = tab.dataset.caleumsGuideTabButton === name;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;

      if (active && focus) {
        tab.focus();
      }
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.caleumsGuidePanel !== name;
    });
  }

  onKeydown(event) {
    const index = this.tabs.findIndex((tab) => tab.dataset.caleumsGuideTabButton === this.selected);
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index + (rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = index + (rtl ? 1 : -1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    next = (next + this.tabs.length) % this.tabs.length;
    this.select(this.tabs[next].dataset.caleumsGuideTabButton, true);
  }
}

if (!window.customElements.get('caleums-size-field')) {
  window.customElements.define('caleums-size-field', CaleumsSizeField);
}

if (!window.customElements.get('caleums-size-guide')) {
  window.customElements.define('caleums-size-guide', CaleumsSizeGuide);
}

// "Size guide" opens the chart, "How to measure" (in the size sheet) opens the measuring steps. Capture phase, so the
// tab is chosen before Prestige opens the modal.
if (!window.caleumsSizeGuideLinks) {
  window.caleumsSizeGuideLinks = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-caleums-guide-tab]');
    const modal = link && document.getElementById(link.getAttribute('aria-controls'));

    if (modal) {
      respectReducedMotion(modal);
      modal.querySelector('caleums-size-guide')?.select?.(link.dataset.caleumsGuideTab);
    }
  }, true);
}
